import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { configItemsToRecord } from '../../../hooks/usePlanogramComplianceConfig';
import { PlanogramComplianceAdvancedSettings } from '../../../pages/store-details/components/PlanogramComplianceAdvancedSettings';
import { PlanogramComplianceConfigItem } from '../../../types';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalModel } from '../../types';

export type PcModelModalMode = 'create' | 'edit' | 'clone';

interface PcModelModalProps {
  mode: PcModelModalMode;
  source?: EvalModel | null; // existing model for edit/clone
  onSave: (model: EvalModel) => void;
  onClose: () => void;
}

// Convert a stored config (snake_case keys, canonical PogComplianceConfig.json() shape) into
// the camelCase-keyed value map the descriptor list uses.
function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
}

function applyStoredConfigToDescriptors(
  descriptors: PlanogramComplianceConfigItem[],
  stored: Record<string, unknown> | null | undefined
): PlanogramComplianceConfigItem[] {
  if (!stored) return descriptors;
  const camelStored: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(stored)) camelStored[snakeToCamel(k)] = v;
  return descriptors.map((item) =>
    item.key in camelStored
      ? { ...item, value: camelStored[item.key] as PlanogramComplianceConfigItem['value'] }
      : item
  );
}

const EMPTY_FORM = {
  model_name: '',
  description: '',
  is_archived: false,
};

export function PcModelModal({ mode, source, onSave, onClose }: PcModelModalProps) {
  const { token } = useAuthContext();
  const initialForm = useMemo(() => {
    if (!source) return EMPTY_FORM;
    if (mode === 'edit') {
      return {
        model_name: source.model_name,
        description: source.description ?? '',
        is_archived: source.is_archived,
      };
    }
    if (mode === 'clone') {
      // Empty identity, carry over description + status as starting points
      return {
        model_name: '',
        description: source.description ?? '',
        is_archived: source.is_archived,
      };
    }
    return EMPTY_FORM;
  }, [mode, source]);

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [config, setConfig] = useState<PlanogramComplianceConfigItem[] | null>(null);
  const [defaultConfig, setDefaultConfig] = useState<PlanogramComplianceConfigItem[] | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const firstEditableRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstEditableRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!token) return;
    EvalApiService.fetchPcConfigDescriptors(token)
      .then((items) => {
        setDefaultConfig(items);
        setConfig(applyStoredConfigToDescriptors(items, source?.params));
      })
      .catch(() => setSubmitError('Failed to load config descriptors'))
      .finally(() => setLoadingConfig(false));
  }, [token, source]);

  const set = (key: keyof typeof EMPTY_FORM, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleConfigChange = (key: string, value: number | boolean | string | string[]) => {
    setConfig((prev) =>
      prev ? prev.map((item) => (item.key === key ? { ...item, value } : item)) : prev
    );
  };

  const handleConfigReset = () => {
    if (defaultConfig) setConfig(defaultConfig);
  };

  const buildConfigPayload = (): Record<string, unknown> | null => {
    if (!config || !defaultConfig) return null;
    const diff = config.filter((item, i) => {
      const def = defaultConfig[i];
      return JSON.stringify(item.value) !== JSON.stringify(def?.value);
    });
    if (diff.length === 0) return null;
    return configItemsToRecord(diff);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSaving(true);
    try {
      const configPayload = buildConfigPayload();
      let model: EvalModel;
      if (mode === 'edit' && source) {
        model = await EvalApiService.patchModel(token ?? '', source.model_id, {
          description: form.description.trim() || null,
          is_archived: form.is_archived,
          // PATCH semantics: send {} (no params field) to leave params untouched.
          // Send null only when the user wants to wipe out overrides — we don't expose that here,
          // so always send the diff (or nothing if no overrides).
          ...(configPayload !== null ? { params: configPayload } : {}),
        });
      } else {
        // create or clone — both POST a new entry
        model = await EvalApiService.createPcModel(token ?? '', {
          model_name: form.model_name.trim(),
          description: form.description.trim() || undefined,
          params: configPayload,
        });
      }
      onSave(model);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save PC model');
    } finally {
      setSaving(false);
    }
  };

  const idLocked = mode === 'edit';
  const canSubmit = (idLocked || form.model_name.trim()) && !saving && !loadingConfig;

  const title =
    mode === 'edit'
      ? `Edit PC Config — ${source?.model_name ?? ''}`
      : mode === 'clone'
        ? `Clone PC Config — from ${source?.model_name ?? ''}`
        : 'New PC Config';

  const submitLabel = saving
    ? 'Saving…'
    : mode === 'edit'
      ? 'Save Changes'
      : mode === 'clone'
        ? 'Create Clone'
        : 'Create';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 28,
          width: 1100,
          maxWidth: '98vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, flexShrink: 0 }}>
          {title}
        </div>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              flexShrink: 0,
            }}
          >
            <div className="eval-form-group">
              <label className="eval-form-label">
                Model Name
                {!idLocked && <span style={{ color: 'var(--danger)' }}> *</span>}
                {idLocked && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                    }}
                  >
                    (immutable)
                  </span>
                )}
              </label>
              <input
                ref={firstEditableRef}
                className="eval-form-input"
                value={form.model_name}
                onChange={(e) => set('model_name', e.target.value)}
                placeholder="e.g. pog_compliance_v3"
                required={!idLocked}
                disabled={idLocked}
                style={
                  idLocked
                    ? { opacity: 0.6, cursor: 'not-allowed', fontFamily: 'var(--font-mono)' }
                    : { fontFamily: 'var(--font-mono)' }
                }
              />
            </div>
            <div className="eval-form-group">
              <label className="eval-form-label">Status</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_archived}
                  onChange={(e) => set('is_archived', e.target.checked)}
                />
                <span style={{ fontSize: 13 }}>Archived</span>
              </label>
            </div>
            <div className="eval-form-group">
              <label className="eval-form-label">Description</label>
              <textarea
                className="eval-form-textarea"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Optional description"
                rows={2}
              />
            </div>
          </div>

          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginTop: 8,
              marginBottom: 8,
              flexShrink: 0,
            }}
          >
            Compliance Config
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 400,
                color: 'var(--text-tertiary)',
              }}
            >
              Only fields that differ from defaults are persisted.
            </span>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              background: 'var(--surface-raised)',
            }}
          >
            {loadingConfig ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Loading config descriptors…
              </div>
            ) : config ? (
              <PlanogramComplianceAdvancedSettings
                config={config}
                defaultConfig={defaultConfig}
                onConfigChange={handleConfigChange}
                onReset={handleConfigReset}
                isV2={false}
              />
            ) : (
              <div style={{ color: 'var(--danger)', fontSize: 13 }}>
                Failed to load config descriptors.
              </div>
            )}
          </div>

          {submitError && (
            <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8, flexShrink: 0 }}>
              {submitError}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              marginTop: 16,
              flexShrink: 0,
            }}
          >
            <button type="button" className="eval-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="eval-btn" disabled={!canSubmit}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
